import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import React, { useEffect, useState } from "react";
import ModalDefault from "../ModalDefault";
import { FooterType } from "../ModalDefault/interface";
import { ModalFormCustomerProps, IFormNumberOfPax } from "./interface";
import TextRequired from "../TextRequired";
import {
  CreateUpdateQuotationItemFlightInput,
  CreateUpdateQuotationItemInput,
} from "../../generated/graphql";
import QuotationItemFlights from "./FieldFlights";
import FormNumberOfPax from "./FormNumberOfPax";
import FieldPassengers from "./FieldPassengers";

const ModalFormQuotationItem: React.FC<ModalFormCustomerProps> = ({
  value,
  isOpen,
  onClose,
  onSubmit,
  ...props
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
    control,
  } = useForm<CreateUpdateQuotationItemInput>();

  const clear = () => {
    setPage(1);
    reset();
  };

  useEffect(() => {
    if (isOpen) {
      reset({
        numberOfPax: value?.numberOfPax,
        airline: value?.airline,
        pricePerPax: value?.pricePerPax,
        class: value?.class,
        passengers: value?.passengers,
        flights: value?.flights,
      });
    }
  }, [value, isOpen]);

  const watchNumberOfPax = watch("numberOfPax");

  const [page, setPage] = useState(1);

  const onSubmitNumberOfPax = (data: IFormNumberOfPax) => {
    setValue("numberOfPax", data.numberOfPax);
    setPage(2);
  };

  useEffect(() => {
    if (value) {
      setPage(2);
    }
  }, [value]);

  return (
    <ModalDefault
      {...props}
      contentMaxW={page === 2 ? "1200px" : "500px"}
      title="Quotation Item"
      isOpen={isOpen}
      hideFooter={page === 1}
      footerType={FooterType.FORM}
      onButtonSaveClick={() => {
        handleSubmit((data) => onSubmit(data, () => clear()))();
      }}
      onClose={() => {
        clear();
        onClose();
      }}
      onButtonCancelClick={() => {
        clear();
        onClose();
      }}
    >
      <>
        {page === 1 && (
          <FormNumberOfPax
            onSubmit={onSubmitNumberOfPax}
            value={value ? { numberOfPax: value.numberOfPax } : undefined}
          />
        )}

        {page === 2 && (
          <form autoComplete="off">
            <Flex gap={4}>
              <FormControl>
                <FormLabel htmlFor="name" size="sm" fontSize="sm" mr="0">
                  <Flex justifyContent="space-between">
                    <Box as="span">
                      Number of People (Pax)<TextRequired>*</TextRequired>
                    </Box>
                    <Box>
                      <Button
                        fontSize="sm"
                        minW="none"
                        variant="link"
                        onClick={() => setPage(1)}
                      >
                        Edit
                      </Button>
                    </Box>
                  </Flex>
                </FormLabel>
                <Input id="name" readOnly {...register("numberOfPax")} />
              </FormControl>
              <FormControl isInvalid={errors.pricePerPax ? true : false}>
                <FormLabel htmlFor="pricePerPax" fontSize="sm">
                  Price per Pax<TextRequired>*</TextRequired>
                </FormLabel>
                <Input
                  id="pricePerPax"
                  {...register("pricePerPax", {
                    required: true,
                    validate: (value) => value > 0,
                    valueAsNumber: true,
                  })}
                />
                <FormErrorMessage>Price Per Pax is required!</FormErrorMessage>
              </FormControl>
            </Flex>
            <Flex gap={4}>
              <FormControl isInvalid={errors.airline ? true : false}>
                <FormLabel htmlFor="airline" size="sm" fontSize="sm">
                  Airline<TextRequired>*</TextRequired>
                </FormLabel>
                <Input
                  id="airline"
                  {...register("airline", { required: true })}
                />
                <FormErrorMessage>Airline is required!</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={errors.class ? true : false}>
                <FormLabel htmlFor="class" fontSize="sm">
                  Class<TextRequired>*</TextRequired>
                </FormLabel>
                <Input id="class" {...register("class", { required: true })} />
                <FormErrorMessage>Class is required!</FormErrorMessage>
              </FormControl>
            </Flex>

            <FormControl isInvalid={errors?.passengers ? true : false}>
              <FormLabel htmlFor="name" size="sm" fontSize="sm">
                Passengers<TextRequired>*</TextRequired>
              </FormLabel>
              <Controller
                name="passengers"
                rules={{
                  required: true,
                  validate: (data) => {
                    return data.every((elm) => elm);
                  },
                }}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <FieldPassengers
                    value={value}
                    isError={error ? true : false}
                    number={watchNumberOfPax}
                    onChange={onChange}
                  />
                )}
              />
              <FormErrorMessage>
                {errors?.flights?.type === "required"
                  ? "Please complete the data!"
                  : ""}
                {errors?.flights?.type === "validate"
                  ? "Please complete the data!"
                  : ""}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors?.flights ? true : false}>
              <Controller
                name="flights"
                control={control}
                rules={{
                  required: true,
                  validate: (data) => {
                    return data.every(
                      (elm) =>
                        elm?.flightNumber &&
                        elm?.travel &&
                        elm?.arrivalTime &&
                        elm?.departTime
                    );
                  },
                }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <QuotationItemFlights
                    value={
                      (value || []) as CreateUpdateQuotationItemFlightInput[]
                    }
                    onChange={onChange}
                    isError={error ? true : false}
                  />
                )}
              />
              <FormErrorMessage>
                {errors?.flights?.type === "required"
                  ? "Please complete the data!"
                  : ""}
                {errors?.flights?.type === "validate"
                  ? "Please complete the data!"
                  : ""}
              </FormErrorMessage>
            </FormControl>
          </form>
        )}
      </>
    </ModalDefault>
  );
};

export default ModalFormQuotationItem;
